'use strict';

import { PubSub } from '../models/common/pubsub';

/**
 * In-memory shared storage across webpack entry bundles (window-backed).
 * Values are live references (mutating a stored object is visible to all readers).
 *
 * `get(key, factory)` is a cache get-or-create: on miss, `factory` runs and the
 * result is stored unless it is `undefined`. Without `factory`, miss → `null`.
 *
 * Well-known slot: {@link sharedStorage.tabChannel} — lazy `PubSub('tab_bus')`
 * singleton for multi-cart cross-tab coordination.
 */
export const sharedStorage = {
  /**
   * Property name on `window` for the shared bag.
   *
   * @readonly
   * @private
   */
  WINDOW_KEY: '__hondaSharedStorage',

  /**
   * Bag key for the storefront tab {@link PubSub} singleton.
   *
   * @readonly
   * @private
   */
  TAB_CHANNEL_KEY: 'tabChannel',

  /**
   * `PubSub` channel name for {@link sharedStorage.tabChannel}.
   *
   * @readonly
   * @private
   */
  TAB_BUS_NAME: 'tab_bus',

  /**
   * Shared cross-tab PubSub for multi-cart coordination (lazy singleton).
   *
   * @returns {import('../models/common/pubsub').PubSub}
   */
  get tabChannel() {
    return this.get(this.TAB_CHANNEL_KEY, () => new PubSub(this.TAB_BUS_NAME));
  },

  /**
   * Lazily create the window-backed bag object.
   *
   * @private
   *
   * @returns {Object}
   */
  get storage() {
    if (!window[this.WINDOW_KEY]) {
      window[this.WINDOW_KEY] = Object.create(null);
    }

    return window[this.WINDOW_KEY];
  },

  /**
   * @param {string} key
   *
   * @returns {boolean} whether `key` exists (including stored `null` / `false`)
   */
  has(key) {
    return Object.hasOwn(this.storage, key);
  },

  /**
   * Read a bag entry, or create it via `factory` on miss.
   *
   * @param {string} key
   * @param {function(): *} [factory] called only on miss; `undefined` is not stored
   *
   * @returns {*} stored value, factory result, or `null` when missing and no factory
   */
  get(key, factory) {
    if (this.has(key)) {
      return this.storage[key];
    }

    if (typeof factory !== 'function') {
      return null;
    }

    const value = factory();

    if (value !== undefined) {
      this.storage[key] = value;
    }

    return value;
  },

  /**
   * Write (or overwrite) a bag entry.
   *
   * @param {string} key
   * @param {*} value
   */
  set(key, value) {
    this.storage[key] = value;
  },

  /**
   * Delete one bag entry.
   *
   * @param {string} key
   */
  remove(key) {
    delete this.storage[key];
  },

  /**
   * Remove every entry from the shared bag (does not destroy `window` bag object).
   */
  clear() {
    this.keys().forEach(key => {
      delete this.storage[key];
    });
  },

  /**
   * @returns {string[]} keys currently stored in the bag
   */
  keys() {
    return Object.keys(this.storage);
  }
};
